import { useCallback, useMemo } from "react";

import { usePricingOptions } from "routes/data-routes/billing-data/pricing-options";
import { useLocalStorage } from "hooks/useLocalStorage";
import { useActiveSubscription } from "routes/data-routes/billing-data/active-subscription";
import { useCallbackAfterSubmission } from "v2/hooks/useCallbackAfterSubmission";
import { useCounters } from "v2/hooks/billing/useCounters";

import { SubscriptionId, SubscriptionTermEnd } from "types/billing/BillingTypes";
import { ConsumptionMilestone, consumptionMilestones } from "types/billing/pricing-options/ConsumptionMilestones";


export interface SADismissedPricingOptionsWarnings {
  [ index: SubscriptionId ]: {
    [ index: SubscriptionTermEnd ]: {
      [ key in `over${ConsumptionMilestone}Dismissed` ]?: boolean;
    }
  }
}

export function usePricingOptionsWarnings() {
  const { activeSubscription } = useActiveSubscription();
  const { counters } = useCounters();
  const { pricingOptions, pricingOptionsFetcher } = usePricingOptions();

  const [ dismissedPricingOptionsWarnings, setDismissedPricingOptionsWarnings ] = useLocalStorage<SADismissedPricingOptionsWarnings>("saPricingOptions", {});

  const consumptionMilestone = useMemo<ConsumptionMilestone | undefined>(() => {
    if ((activeSubscription?.apiCreditLimit !== undefined) && (counters?.requestCount !== undefined)) {
      const usage = (counters.requestCount * 100) / activeSubscription.apiCreditLimit;
      return consumptionMilestones.find(milestone => usage >= milestone);
    }
  }, [ activeSubscription?.apiCreditLimit, counters?.requestCount ]);

  const isDismissed = useMemo(() => {
    if (activeSubscription?.billingSubscriptionId && consumptionMilestone) {
      const dismissedPricingOptions = dismissedPricingOptionsWarnings[activeSubscription.billingSubscriptionId]?.[activeSubscription.currentTermEnd];
      return dismissedPricingOptions?.[`over${consumptionMilestone}Dismissed`] ?? false;
    }
  }, [ activeSubscription, consumptionMilestone, dismissedPricingOptionsWarnings ]);

  const dismissWarningMilestone = useCallback(() => {
    if (activeSubscription?.billingSubscriptionId && consumptionMilestone) {
      setDismissedPricingOptionsWarnings(dismissedOptions => {
        if (!dismissedOptions[activeSubscription.billingSubscriptionId]) {
          dismissedOptions[activeSubscription.billingSubscriptionId] = {};
        }
        if (!dismissedOptions[activeSubscription.billingSubscriptionId][activeSubscription.currentTermEnd]) {
          dismissedOptions[activeSubscription.billingSubscriptionId][activeSubscription.currentTermEnd] = {};
        }
        dismissedOptions[activeSubscription.billingSubscriptionId][activeSubscription.currentTermEnd][`over${ consumptionMilestone }Dismissed`] = true;

        // TODO should use the useImmerReducer hook here? check use-immer
        return { ...dismissedOptions }; // return with a new object here, otherwise the 'isDismissed' memo won't get updated
      });
    }
  }, [ activeSubscription, consumptionMilestone, setDismissedPricingOptionsWarnings ]);

  // auto-dismiss milestone after a feature is turned on, so turning it off again won't pop up the same dialog immediately
  const autoDismissCallback = useCallback(() => {
    if (pricingOptions?.["pay-as-you-go"]?.enabled || pricingOptions?.["auto-upgrade"]?.enabled) {
      dismissWarningMilestone();
    }
  }, [ pricingOptions, dismissWarningMilestone ]);
  useCallbackAfterSubmission(autoDismissCallback, pricingOptionsFetcher);


  return {
    consumptionMilestone,
    isWarningMilestoneDismissed: isDismissed,
    dismissWarningMilestone,
  };
}
